import { ArrowUpRight, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { usePrivacy } from '@/lib/privacy';
import { Button, Card, Bar, ErrorBox } from './ui';
import s from './SterlingScore.module.css';
type Score = {
  score: number | null;
  tier: string;
  coveragePct: number;
  provisional: boolean;
  from: string;
  to: string;
  transactionCount: number;
  minimum: string | null;
  components: Array<{
    key: string;
    label: string;
    weight: number;
    value: number | null;
    detail: string;
    href: string;
  }>;
  next: { label: string; href: string };
};
export function SterlingScore() {
  const navigate = useNavigate(),
    { masked } = usePrivacy(),
    query = useQuery({
      queryKey: ['sterling-score'],
      queryFn: () => api<Score>('/analytics/score'),
    }),
    data = query.data;
  if (query.error)
    return (
      <ErrorBox>Couldn’t calculate your habits score. {(query.error as Error).message}</ErrorBox>
    );
  return (
    <Card className={s.hero}>
      <div className={s.top}>
        <div className={s.copy}>
          <span className={s.eyebrow}>
            <Sparkles size={14} aria-hidden="true" /> YOUR STERLING SCORE
          </span>
          <h2>{data?.tier ?? 'Getting your picture together'}</h2>
          <p>
            A little more clarity. A little more control.
            <br />
            Build habits that move your money forward.
          </p>
          <div className={s.actions}>
            <Button
              variant="primary"
              onClick={() => navigate(data?.next.href ?? '/app/transactions')}
            >
              {data?.score === null ? 'Build my score' : 'My next step'}{' '}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Button>
            <Button variant="ghost" onClick={() => navigate('/app/budgets')}>
              My monthly plan
            </Button>
          </div>
        </div>
        <div className={s.gauge}>
          <svg viewBox="0 0 180 180" aria-hidden="true">
            <circle cx="90" cy="90" r="76" fill="none" stroke="var(--border)" strokeWidth="8" />
            <circle
              className={s.scoreArc}
              cx="90"
              cy="90"
              r="76"
              fill="none"
              stroke="var(--accent-hi)"
              strokeWidth="8"
              pathLength="100"
              strokeDasharray={`${masked ? 0 : (data?.score ?? 0)} 100`}
              strokeLinecap="round"
              transform="rotate(-90 90 90)"
            />
          </svg>
          <div>
            <strong>{masked ? '•••' : (data?.score ?? '—')}</strong>
            <span>{data?.score == null ? 'Building habits' : 'out of 100'}</span>
          </div>
        </div>
      </div>
      {data ? (
        <>
          <div className={s.progress}>
            {data.components.map((component, index) => (
              <button
                className={s.habit}
                key={component.key}
                onClick={() => navigate(component.href)}
              >
                <span>
                  <b className={s.habitNumber}>0{index + 1}</b>
                  {component.label} <ArrowUpRight size={14} aria-hidden="true" />
                </span>
                <Bar color="var(--accent-hi)" pct={masked ? 0 : (component.value ?? 0)} />
                <small>
                  {masked
                    ? 'Hidden'
                    : component.value === null
                      ? 'Needs more data'
                      : `${component.value}/100`}
                </small>
              </button>
            ))}
          </div>
          <div className={s.next}>
            <span>
              {masked
                ? 'Reveal your amounts to see your personal next step.'
                : (data.minimum ?? data.next.label)}
            </span>
            <small>
              {data.provisional
                ? `Provisional · ${data.coveragePct}% of habit weights measured`
                : 'All four habits measured'}{' '}
              · Last 90 days + current budget
            </small>
          </div>
          <details className={s.method}>
            <summary>How your score works</summary>
            <p>
              This is Sterling’s own financial habits indicator, not a credit score or lending
              decision. Missing data is excluded and available weights are normalized. At least five
              transactions and two measurable habits are needed.
            </p>
            {data.components.map((c) => (
              <p key={c.key}>
                <strong>
                  {c.label} ({c.weight}% weight).
                </strong>{' '}
                {masked ? 'Reveal your amounts to see the breakdown.' : c.detail}
              </p>
            ))}
            <p>
              Savings earns full points at your Settings target; zero or negative retention earns
              zero. Budget earns full points within the monthly spending allowance and falls
              linearly to zero at twice that allowance. Clarity measures categorized or explicitly
              reviewed transactions. Repayment measures your own confirmed on-time borrowing; other
              people owing you never lowers your score. Splits use your share and refunds reduce
              spending. Score version 1.
            </p>
          </details>
        </>
      ) : null}
    </Card>
  );
}
